// PlanSummary.jsx
import React, { useState } from "react";
import {
  Card, Tag, Space, Typography, Empty, theme, Button, Row, Col,
  Grid
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import PlanDrawer, { SECTION_LIST } from "../OptionCard";
import NoteDrawer from "../NoteDrawer";

const { Title, Text, Paragraph } = Typography;


const DEMO_ITEMS = [
  // 7 · S.M.D -> simple strings
  { sectionId: 7, choices: ["done"] },

  // 9 · Tip plasty -> mix of simple + with sides (R/L)
  {
    sectionId: 9,
    choices: [
      { code: "twisted", sides: ["R", "L"] },
      { code: "broad" },
    ],
  },

  // 6 · Grafts -> sides + variants
  {
    sectionId: 6,
    choices: [
      { code: "spreader_graft", sides: ["R"] },
      { code: "shield_graft", variants: ["upper", "long"] },
    ],
  },

  // 5 · Nasal obstruction -> severity
  {
    sectionId: 5,
    choices: [{ code: "left_obstruction", severity: "complete" }],
  },

  // 10 · Nostrils -> sides + simple
  {
    sectionId: 10,
    choices: [{ code: "wide", sides: ["R"] }, { code: "asymmetry" }],
  },

  // 2 · Hump -> simple
  { sectionId: 2, choices: [{ code: "Right" }] },
];

const SECTION_BY_ID = SECTION_LIST.reduce((m, s) => {
  m[s.id] = s;
  return m;
}, {});
const findOptionLabel = (sectionId, code) =>
  SECTION_BY_ID[sectionId]?.options?.find((o) => o.code === code)?.label || code;
const findVariantLabel = (sectionId, code, variantCode) =>
  SECTION_BY_ID[sectionId]?.options?.find((o) => o.code === code)?.variants?.find((v) => v.code === variantCode)?.label ||
  variantCode;

// Helper -> stringify a normalized item
const itemToText = (item) => {
  const label = SECTION_BY_ID[item.sectionId]?.label || `Section ${item.sectionId}`;
  const parts = (item.choices || [])
    .map((c) => {
      if (typeof c === "string") return findOptionLabel(item.sectionId, c);
      let t = findOptionLabel(item.sectionId, c.code);
      if (Array.isArray(c.sides) && c.sides.length) t += ` (${c.sides.join("/")})`;
      if (c.severity) t += ` · ${c.severity}`;
      if (Array.isArray(c.variants) && c.variants.length) {
        t += ` · ${c.variants.map((v) => findVariantLabel(item.sectionId, c.code, v)).join("+")}`;
      }
      return t;
    })
    .join("; ");
  return { label, parts };
};

export default function PlanSummary() {
  const { token } = theme.useToken();

  // drawers
  const [open, setOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  // data
  const [items, setItems] = useState([]);          // normalized items from PlanDrawer
  const [successRate, setSuccessRate] = useState(); // number | string
  const [companion, setCompanion] = useState("");   // string
  const [note, setNote] = useState("Test Page");             // string

  const handlePlanSubmit = (data) => {
    // Accept either array or object shape
    if (Array.isArray(data)) {
      setItems(data);
    } else {
      setItems(data.items || []);
      if (data.successRate !== undefined) setSuccessRate(data.successRate);
      if (data.companion !== undefined) setCompanion(data.companion);
    }
    setOpen(false);
  };
 const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // stack for < md
  return (
    <>
      {/* Header */}
  <Row
          className="tab-head"
          style={{ marginBottom: 12, borderRadius: 6, padding: 10, background: token.colorBgContainer }}
          align="middle">
      <Title level={4} style={{ margin: 0 }}>وردە کاری کار</Title>
        <Space
          className="tab-head-action"
          direction={isMobile ? "vertical" : "horizontal"}
          size="small"
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          <Button block={isMobile} onClick={() => setOpen(true)} type="primary">
            {items.length ? "نوێکردنەوە" : "زیادکردن"} {items.length ? <EditOutlined /> : <PlusOutlined />}
          </Button>

          <Button block={isMobile} onClick={() => setNoteOpen(true)}>
            نوێکردنەوەی تێبینی
          </Button>
        </Space>
    </Row>

      {/* Card 1 — Success rate & Patient companion */}
      <Card title="زانیاری گشتی" style={{ marginBottom: 6 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Text strong>Success rate: </Text>
            {successRate !== undefined && successRate !== ""
              ? <Tag color="green">{String(successRate).trim().replace(/%?$/, "%")}</Tag>
              : <Text type="secondary">80%</Text>}
          </Col>
          <Col xs={24} md={12}>
            <Text strong>Patient’s companion: </Text>
            {companion ? <Tag>{companion}</Tag> : <Text type="secondary">ئالان عوسمان کەریم</Text>}
          </Col>
        </Row>
      </Card>

      {/* Card 2 — Plan (section name colored) */}
     <Card title="پلانی کار" style={{ marginBottom: 6 }}>
   {DEMO_ITEMS.length === 0 ? (
     <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No selections yet" />
  ) : (
     <div>
       {DEMO_ITEMS.map((it, idx) => {
         const { label, parts } = itemToText(it);
         return (
           <Paragraph key={it.sectionId ?? idx} style={{ marginBottom: 4 }}>
             <Text strong style={{ color: token.colorPrimary }}>{label}</Text>
             <Text>
               : {parts || <span style={{ color: token.colorTextQuaternary }}>No selections.</span>}
             </Text>
           </Paragraph>
         );
       })}
     </div>
   )}
 </Card>

      {/* Card 3 — Note */}
      <Card title="تێبینی">
        {note ? (
          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{note}</Paragraph>
        ) : (
          <Text type="secondary">No note.</Text>
        )}
      </Card>

      {/* Drawers */}
      <PlanDrawer open={open} onClose={() => setOpen(false)} onSubmit={handlePlanSubmit} />
      <NoteDrawer
        open={noteOpen}
        initialNote={note}
        onClose={() => setNoteOpen(false)}
        onSubmit={(val) => setNote(val)}
      />
    </>
  );
}
